import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartLine, FaClock, FaUsers, FaLightbulb, FaChartBar, FaComments, FaArrowRight } from 'react-icons/fa';
import { useLang } from '../contexts/LanguageContext';
import '../styles/homepage.css';

const CONTENT = {
  en: {
    badge: 'Participatory Modelling Evaluation Tool',
    h1: <>The Dartboard Evaluation Tool<br />— now digital</>,
    subtitle: 'A structured evaluation tool for participatory modelling, land use planning and spatial decision-making — and equally at home in team workshops, training sessions and any group where you need to collect visual, real-time feedback. Define your criteria, share a link, and visualise collective responses on the target instantly.',
    cta: 'Create Your Dartboard',
    featuresTitle: 'How it works',
    features: [
      { title: 'Visual & Interactive', desc: 'Stakeholders click the ring that matches their assessment — just like placing a sticker on the physical dartboard used in participatory modelling sessions' },
      { title: 'Real-Time Results', desc: 'Collective responses accumulate on the target as participants submit, enabling immediate discussion and deliberation' },
      { title: 'Zero Friction', desc: 'Each participant accesses via a unique link — no account, no app, no installation required' },
    ],
    stepsTitle: 'Three simple steps',
    steps: [
      { n: '1', title: 'Define your criteria', desc: 'Add the evaluation dimensions for your session — land use indicators, modelling outputs, process quality criteria, or any other assessment framework (up to 8)' },
      { n: '2', title: 'Share with stakeholders', desc: 'Each participant rates from their own device via a unique shareable link' },
      { n: '3', title: 'Read the results', desc: 'Collective responses appear as dots on the target and can be exported to Excel for further analysis' },
    ],
    usesTitle: 'Works great for',
    uses: [
      { title: 'Participatory Modelling', desc: 'Multi-stakeholder evaluation in land use, spatial modelling and resilience processes' },
      { title: 'Team Workshops', desc: 'Collect structured group feedback on any topic — quick to set up, easy for everyone to use' },
      { title: 'Training & Education', desc: 'Measure learning outcomes, capture group perspectives and spark discussion' },
      { title: 'Planning & Research', desc: 'Scenario assessment, bottom-up spatial planning and collaborative decision-making sessions' },
    ],
    ctaTitle: 'Ready to get started?',
    ctaDesc: 'Create your first digital dartboard and bring structure to your next participatory modelling session',
    ctaBtn: 'Start Now',
    demoLabels: ['Engagement', 'Land Use', 'Resilience', 'Process'],
  },
  pt: {
    badge: 'Ferramenta de Avaliação Participativa',
    h1: <>O Dartboard de Avaliação<br />— agora digital</>,
    subtitle: 'Uma ferramenta de avaliação estruturada para modelação participativa, planeamento do uso do solo e decisão espacial — e igualmente útil em workshops de equipa, formações e qualquer grupo onde precise de recolher feedback visual em tempo real. Defina os critérios, partilhe um link e visualize as respostas coletivas no alvo instantaneamente.',
    cta: 'Criar Dartboard',
    featuresTitle: 'Como funciona',
    features: [
      { title: 'Visual e interativo', desc: 'Os participantes clicam no anel que corresponde à sua avaliação — tal como colocar um sticker no quadro físico' },
      { title: 'Resultados em tempo real', desc: 'As respostas acumulam-se no mapa do alvo à medida que os participantes submetem' },
      { title: 'Sem fricção', desc: 'Cada participante acede por link único — sem conta nem instalação necessária' },
    ],
    stepsTitle: 'Três passos simples',
    steps: [
      { n: '1', title: 'Defina os critérios', desc: 'Adicione as dimensões de avaliação da sua sessão (até 8)' },
      { n: '2', title: 'Partilhe com os participantes', desc: 'Cada pessoa avalia a partir do seu dispositivo via link único' },
      { n: '3', title: 'Leia os resultados', desc: 'As respostas aparecem como pontos no alvo e podem ser exportadas para Excel' },
    ],
    usesTitle: 'Ideal para',
    uses: [
      { title: 'Modelação Participativa', desc: 'Avaliação multi-stakeholder em processos de uso do solo, modelação espacial e resiliência' },
      { title: 'Workshops de equipa', desc: 'Recolher feedback estruturado de grupos em qualquer tema — rápido de criar, fácil de usar' },
      { title: 'Formação e educação', desc: 'Medir resultados de aprendizagem e captar perspetivas de grupo' },
      { title: 'Planeamento e investigação', desc: 'Avaliação de cenários, planeamento espacial participativo e tomada de decisão colaborativa' },
    ],
    ctaTitle: 'Pronto para começar?',
    ctaDesc: 'Crie o seu primeiro dartboard digital e leve o processo participativo mais longe',
    ctaBtn: 'Começar agora',
    demoLabels: ['Participação', 'Processo', 'Impacto', 'Recursos'],
  },
};

const HeroDartboard = ({ labels }) => {
  const [tr, br, bl, tl] = labels;
  return (
    <svg
      viewBox="0 0 500 500"
      xmlns="http://www.w3.org/2000/svg"
      style={{ maxWidth: '440px', width: '100%', filter: 'drop-shadow(0 20px 60px rgba(26,26,46,0.22))' }}
      aria-hidden="true"
    >
      <defs>
        <clipPath id="dart-clip">
          <circle cx="250" cy="250" r="193"/>
        </clipPath>
      </defs>

      {/* Rings — outer to inner */}
      <circle cx="250" cy="250" r="193" fill="#c0392b"/>
      <circle cx="250" cy="250" r="155" fill="#e67e22"/>
      <circle cx="250" cy="250" r="117" fill="#f1c40f"/>
      <circle cx="250" cy="250" r="79"  fill="#27ae60"/>
      <circle cx="250" cy="250" r="41"  fill="#1a6b3a"/>

      {/* Ring borders */}
      {[193, 155, 117, 79, 41].map(r => (
        <circle key={r} cx="250" cy="250" r={r} fill="none" stroke="white" strokeWidth="1.5" opacity="0.35"/>
      ))}

      {/* Sector lines clipped to circle */}
      <g clipPath="url(#dart-clip)">
        <line x1="250" y1="57" x2="250" y2="443" stroke="white" strokeWidth="1.5" opacity="0.55"/>
        <line x1="57"  y1="250" x2="443" y2="250" stroke="white" strokeWidth="1.5" opacity="0.55"/>
      </g>

      {/* Ring number labels (right side of horizontal axis) */}
      {[
        { score: 1, x: 424 },
        { score: 2, x: 386 },
        { score: 3, x: 348 },
        { score: 4, x: 310 },
        { score: 5, x: 271 },
      ].map(({ score, x }) => (
        <text key={score} x={x} y="246" textAnchor="middle"
              fill={score === 3 ? '#333' : 'white'} fontSize="12" fontWeight="bold">
          {score}
        </text>
      ))}

      {/* Response dots — 3 users × 4 criteria */}
      {/* Blue user */}
      <circle cx="292" cy="208" r="5.5" fill="#3b82f6" stroke="white" strokeWidth="1.5"/>
      <circle cx="263" cy="263" r="5.5" fill="#3b82f6" stroke="white" strokeWidth="1.5"/>
      <circle cx="183" cy="317" r="5.5" fill="#3b82f6" stroke="white" strokeWidth="1.5"/>
      <circle cx="208" cy="208" r="5.5" fill="#3b82f6" stroke="white" strokeWidth="1.5"/>
      {/* Purple user */}
      <circle cx="319" cy="181" r="5.5" fill="#8b5cf6" stroke="white" strokeWidth="1.5"/>
      <circle cx="292" cy="292" r="5.5" fill="#8b5cf6" stroke="white" strokeWidth="1.5"/>
      <circle cx="208" cy="292" r="5.5" fill="#8b5cf6" stroke="white" strokeWidth="1.5"/>
      <circle cx="236" cy="236" r="5.5" fill="#8b5cf6" stroke="white" strokeWidth="1.5"/>
      {/* Orange user */}
      <circle cx="264" cy="236" r="5.5" fill="#f97316" stroke="white" strokeWidth="1.5"/>
      <circle cx="319" cy="319" r="5.5" fill="#f97316" stroke="white" strokeWidth="1.5"/>
      <circle cx="236" cy="264" r="5.5" fill="#f97316" stroke="white" strokeWidth="1.5"/>
      <circle cx="181" cy="181" r="5.5" fill="#f97316" stroke="white" strokeWidth="1.5"/>

      {/* Criterion labels — outside each sector */}
      <text x="388" y="92"  textAnchor="middle" fill="#1a1a2e" fontSize="12" fontWeight="600">{tr}</text>
      <text x="388" y="418" textAnchor="middle" fill="#1a1a2e" fontSize="12" fontWeight="600">{br}</text>
      <text x="112" y="418" textAnchor="middle" fill="#1a1a2e" fontSize="12" fontWeight="600">{bl}</text>
      <text x="112" y="92"  textAnchor="middle" fill="#1a1a2e" fontSize="12" fontWeight="600">{tl}</text>

      {/* Participant legend */}
      <circle cx="30" cy="468" r="5" fill="#3b82f6" stroke="white" strokeWidth="1"/>
      <circle cx="52" cy="468" r="5" fill="#8b5cf6" stroke="white" strokeWidth="1"/>
      <circle cx="74" cy="468" r="5" fill="#f97316" stroke="white" strokeWidth="1"/>
      <text x="85" y="472" fill="#666" fontSize="10">3 participants</text>
    </svg>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const c = CONTENT[lang];

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="home-hero-badge">{c.badge}</div>
          <h1>{c.h1}</h1>
          <p className="home-hero-subtitle">{c.subtitle}</p>
          <div className="home-hero-buttons">
            <button onClick={() => navigate('/create')} className="home-cta-button home-primary">
              {c.cta} <FaArrowRight className="home-button-icon" />
            </button>
          </div>
        </div>
        <div className="home-hero-visual">
          <HeroDartboard labels={c.demoLabels} />
        </div>
      </section>

      <section className="home-features">
        <h2>{c.featuresTitle}</h2>
        <div className="home-features-grid">
          <div className="home-feature-card">
            <div className="home-feature-icon-wrapper"><FaChartLine className="home-feature-icon" /></div>
            <h3>{c.features[0].title}</h3>
            <p>{c.features[0].desc}</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon-wrapper"><FaClock className="home-feature-icon" /></div>
            <h3>{c.features[1].title}</h3>
            <p>{c.features[1].desc}</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon-wrapper"><FaUsers className="home-feature-icon" /></div>
            <h3>{c.features[2].title}</h3>
            <p>{c.features[2].desc}</p>
          </div>
        </div>
      </section>

      <section className="home-how-it-works">
        <h2>{c.stepsTitle}</h2>
        <div className="home-steps">
          {c.steps.map(s => (
            <div key={s.n} className="home-step">
              <div className="home-step-number">{s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="home-use-cases">
        <h2>{c.usesTitle}</h2>
        <div className="home-use-cases-grid">
          {[FaLightbulb, FaChartBar, FaUsers, FaComments].map((Icon, i) => (
            <div key={i} className="home-use-case-card">
              <div className="home-use-case-icon-wrapper"><Icon className="home-use-case-icon" /></div>
              <h3>{c.uses[i].title}</h3>
              <p>{c.uses[i].desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="home-cta-section">
        <div className="home-cta-content-last">
          <h2>{c.ctaTitle}</h2>
          <p>{c.ctaDesc}</p>
          <button onClick={() => navigate('/create')} className="home-cta-button home-primary home-large">
            {c.ctaBtn} <FaArrowRight className="home-button-icon" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
